import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../../lib/utils';
import './home-product-rail.css';

type HomeProductRailProps = {
  children: React.ReactNode;
  className?: string;
  /** Reset scroll position when filter/tab changes. */
  resetKey?: string;
  'aria-label'?: string;
};

/**
 * Horizontal snap rail: swipe on touch; desktop prev/next sit above the rail
 * (placed in the page right after the View all CTA).
 */
export function HomeProductRail({
  children,
  className,
  resetKey,
  'aria-label': ariaLabel,
}: HomeProductRailProps) {
  const railRef = useRef<HTMLDivElement>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const syncScrollState = useCallback(() => {
    const el = railRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(max > 4 && el.scrollLeft < max - 4);
  }, []);

  useEffect(() => {
    const el = railRef.current;
    if (!el) return;
    el.scrollLeft = 0;
    syncScrollState();
  }, [resetKey, children, syncScrollState]);

  useEffect(() => {
    const el = railRef.current;
    if (!el) return;
    syncScrollState();
    el.addEventListener('scroll', syncScrollState, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(syncScrollState) : null;
    ro?.observe(el);
    window.addEventListener('resize', syncScrollState);
    return () => {
      el.removeEventListener('scroll', syncScrollState);
      ro?.disconnect();
      window.removeEventListener('resize', syncScrollState);
    };
  }, [syncScrollState]);

  const scrollByCard = (dir: -1 | 1) => {
    const el = railRef.current;
    if (!el) return;
    const item = el.querySelector('.home-product-rail-item') as HTMLElement | null;
    const step = item ? item.offsetWidth + 20 : Math.max(280, el.clientWidth * 0.7);
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  return (
    <div className={cn('home-product-rail-shell', className)}>
      <div className="home-product-rail-controls" role="group" aria-label="Browse products">
        <button
          type="button"
          className="home-product-rail-nav"
          aria-label="Scroll products left"
          disabled={!canPrev}
          onClick={() => scrollByCard(-1)}
        >
          <ChevronLeft className="h-5 w-5" aria-hidden />
        </button>
        <button
          type="button"
          className="home-product-rail-nav"
          aria-label="Scroll products right"
          disabled={!canNext}
          onClick={() => scrollByCard(1)}
        >
          <ChevronRight className="h-5 w-5" aria-hidden />
        </button>
      </div>

      <div ref={railRef} className="home-product-rail" role="list" aria-label={ariaLabel}>
        {React.Children.map(children, (child, index) => {
          if (!React.isValidElement(child)) return child;
          return (
            <div key={child.key ?? index} className="home-product-rail-item" role="listitem">
              {child}
            </div>
          );
        })}
      </div>
    </div>
  );
}
